import Vue from 'vue'
import ApiService from '@/common/api.service'

export const state = {
  /*
   * unitSelectionSets is a "grouping" of multiple units that are
   * selected. For example, one might wish to have a "global" set of
   * selected units that is persistent across all screens or one
   * might want to create a set of selected units for a particular
   * individual/group of widgets.
   */
  unitSelectionSets: {},
  queryByClient: null
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @param state Object
   *
   * @return Object
   */
  get (state) {
    return state
  },

  /**
   * Returns the sets of selected units.
   *
   * @param state Object
   *
   * @return Object
   */
  unitSelectionSets (state) {
    return state.unitSelectionSets
  },

  /**
   * Returns either a client object or null depending on whether
   * queries should be made by client.
   *
   * @param state
   *
   * @return {Object|null}
   */
  getClient (state) {
    return state.queryByClient
  }
}

export const actions = {
  /**
   * Query/find one or more units.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  query (context, params) {
    const queryByClient = context.getters['getClient']
    if (queryByClient !== null) {
      return ApiService
        .get(`units/by-client/${queryByClient.id}`, params)
        .then((response) => {
          return response
        })
    } else {
      return ApiService
        .get(`units`, params)
        .then((response) => {
          return response
        })
    }
  },

  /**
   * Query/find recently monitored units.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  queryRecentlyMonitored (context, params) {
    return ApiService
      .get(`users/recent/monitoring/units`, params)
      .then((response) => {
        return response
      })
  },

  /**
   * Query/find units based on their id's.
   *
   * @param context Object
   * @param unitIds Array of unit id's
   *
   * @return Promise
   */
  queryByUnitIds (context, unitIds) {
    return ApiService
      .get(`units`, { id: unitIds.join(';') })
  }
}

export const mutations = {
  /**
   * Add a map or array of object units that have been selected within
   * the specified set.
   *
   * @param state    Object
   * @param set      String
   * @param vehicles Array|Map
   */
  addSelectedUnits (state, { set, units }) {
    if (!state.unitSelectionSets.hasOwnProperty(set)) {
      Vue.set(state.unitSelectionSets, set, {})
    }

    units.forEach(unit => {
      Vue.set(state.unitSelectionSets[set], unit.id, unit)
    })
  },

  /**
   * Removes an array of units from the set's list of selected units.
   * The array must contain the list units that are no longer selected.
   *
   * @param state Object
   * @param set   String
   * @param units Array
   */
  removeSelectedUnits (state, { set, units }) {
    if (state.unitSelectionSets.hasOwnProperty(set)) {
      units.forEach(unit => {
        Vue.delete(state.unitSelectionSets[set], unit.id)
      })
    }
  },

  /**
   * Clears all selected units.
   *
   * @param state Object
   */
  clearAllSelectedUnits (state) {
    Vue.set(state, 'unitSelectionSets', {})
  },

  /**
   * Clears all selected units within a specified set.
   *
   * @param state Object
   * @param set   String
   */
  clearSelectedUnits (state, set) {
    if (state.unitSelectionSets.hasOwnProperty(set)) {
      Vue.set(state.unitSelectionSets, set, {})
    }
  },

  /**
   * Purge the module's state.
   *
   * @param state Object
   */
  purge (state) {
    state.unitSelectionSets = {}
    state.queryByClient = null
  },

  /**
   * Sets the client for which the vehicles should be queried.
   *
   * @param state
   *
   * @param client Object|null
   */
  setClient (state, client) {
    state.queryByClient = client
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}



// WEBPACK FOOTER //
// ./src/store/units/findUnitsWidgetModule.js